import { CountryIcon, Header, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useGeoIpInfo from '../../../lib/hooks/useGeoIpInfo';
import { canonicalizeString } from '../../../lib/utils';
import { COUNTRY_CODES } from '../../constants';
import caProvinces from '../../constants/caProvinces';
import usStates from '../../constants/usStates';
import CountryPropTypes from '../../prop-types/country';
import DataGrid from '../DataGrid';
import styles from './RegionContainer.module.css';

const cx = classnames.bind(styles);

function getFlagHeader(t, countryInfo) {
  const { regionTerm, code3Chars } = countryInfo;
  return (
    <Header as="h2" color="grey" className={cx('flagHeader')}>
      <CountryIcon countryCode={code3Chars} size="xl" />
      <Text className={cx('flagTitle')}>
        {regionTerm === 'Province'
          ? t('chooseAProvince', { ns: 'location' })
          : t('chooseAState', { ns: 'location' })}
      </Text>
    </Header>
  );
}

function getHeader(t, countryInfo, isFlagInHeader) {
  return isFlagInHeader
    ? getFlagHeader(t, countryInfo)
    : t(`findCheapGas${countryInfo.code3Chars}`, { ns: 'price' });
}

export default function RegionContainer({ country, isFlagInHeader }) {
  const { t } = useTranslation(['location', 'address']);

  const COUNTRY_INFO = useMemo(() => ({
    [COUNTRY_CODES.USA]: {
      countryCode: COUNTRY_CODES.USA,
      code3Chars: 'USA',
      regionTerm: 'State',
      friendlyName: 'the USA',
      regions: usStates.map(({ name, abbr }) => ({
        key: name,
        displayText: t(`address:regions.US.${abbr}`),
        url: canonicalizeString(`/gasprices/${name}`),
      })),
    },
    [COUNTRY_CODES.CANADA]: {
      countryCode: COUNTRY_CODES.CANADA,
      code3Chars: 'CAN',
      regionTerm: 'Province',
      friendlyName: 'Canada',
      regions: caProvinces.map(({ name, abbr }) => ({
        key: name,
        displayText: t(`address:regions.CA.${abbr}`),
        url: canonicalizeString(`/gasprices/${name}`),
      })),
    },
  }), [t]);

  const geoIp = useGeoIpInfo();
  const countryCode = country || geoIp.country;
  // ensure setting country info only for NAM country codes
  const countryInfo = countryCode === COUNTRY_CODES.CANADA ? COUNTRY_INFO[COUNTRY_CODES.CANADA] : COUNTRY_INFO[COUNTRY_CODES.USA];
  const { regions } = countryInfo;

  const analytics = useAnalytics();
  const header = getHeader(t, countryInfo, isFlagInHeader);
  const columnProps = isFlagInHeader ? { mobile: 6, tablet: 4 } : { mobile: 6 };

  const handleItemClick = useCallback(({ displayText: regionName }) => {
    analytics.tagEvent({
      name: 'SearchList_StateLink',
      attributes: { State: regionName },
    });
  }, [analytics]);

  return (
    <DataGrid
      header={header}
      items={regions}
      onItemClick={handleItemClick}
      {...columnProps}
    />
  );
}

RegionContainer.propTypes = {
  country: CountryPropTypes,
  isFlagInHeader: PropTypes.bool,
};

RegionContainer.defaultProps = {
  country: undefined,
  isFlagInHeader: false,
};
